// AGC: AddressAutocomplete - Address search with Nominatim API
// AGC: Provides autocomplete functionality for address input

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Search, MapPin, Loader2, X } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface AddressResult {
  place_id: string
  display_name: string
  lat: string
  lon: string
  address: {
    city?: string
    town?: string
    village?: string
    state?: string
    country?: string
    country_code?: string
  }
}

interface AddressAutocompleteProps {
  countryCode: string // AGC: 'BR' or 'AR'
  onSelect: (result: AddressResult) => void
  placeholder?: string
  className?: string
}

// =============================================================================
// AGC: HELPER FUNCTIONS
// =============================================================================

/**
 * AGC: Search addresses using Nominatim API
 * AGC: Rate limit: 1 request per second (handled by debounce)
 */
const searchAddresses = async (
  query: string,
  countryCode: string
): Promise<AddressResult[]> => {
  if (!query || query.length < 3) return []
  
  try {
    const url = `https://nominatim.openstreetmap.org/search?` + new URLSearchParams({
      format: 'json',
      q: query,
      countrycodes: countryCode.toLowerCase(),
      limit: '5',
      addressdetails: '1',
    })
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'DroidFarm/1.0',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    console.log('AGC: Nominatim results:', data)
    
    return data as AddressResult[]
  } catch (error) {
    console.error('AGC: Error searching addresses:', error)
    return []
  }
}

/**
 * AGC: Debounce hook
 */
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  
  return debouncedValue
}

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  countryCode,
  onSelect,
  placeholder,
  className = '',
}) => {
  const { t } = useTranslation()
  
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AddressResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // AGC: Debounce search query to avoid rate limiting
  const debouncedQuery = useDebounce(query, 300)
  
  // AGC: Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) {
      setResults([])
      setIsSearching(false)
      return
    }
    
    const performSearch = async () => {
      setIsSearching(true)
      
      try {
        const searchResults = await searchAddresses(debouncedQuery, countryCode)
        setResults(searchResults)
        setIsOpen(true)
      } catch (error) {
        console.error('AGC: Search error:', error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }
    
    performSearch()
  }, [debouncedQuery, countryCode])
  
  // AGC: Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const handleSelect = (result: AddressResult) => {
    setQuery(result.display_name)
    setIsOpen(false)
    setSelectedIndex(-1)
    onSelect(result)
  }
  
  const handleClear = () => {
    setQuery('')
    setResults([])
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }
  
  // AGC: Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }
  
  // AGC: Format display name (shorten long addresses)
  const formatDisplayName = (displayName: string): string => {
    const parts = displayName.split(',')
    if (parts.length > 4) {
      return parts.slice(0, 3).join(',') + '...'
    }
    return displayName
  }
  
  return (
    <div className={`relative ${className}`}>
      {/* AGC: Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true)
          }}
          placeholder={placeholder || t('wizard.location.addressPlaceholder')}
          className="w-full pl-10 pr-10 py-3 bg-white text-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent dark:bg-gray-800 dark:text-white"
          autoComplete="off"
        />
        
        {/* AGC: Loading or Clear button */}
        {isSearching ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-gray-400" />
        ) : query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      {/* AGC: Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-80 overflow-y-auto"
        >
          {results.map((result, index) => {
            const cityName = result.address.city || result.address.town || result.address.village
            const state = result.address.state
            
            return (
              <button
                key={result.place_id}
                type="button"
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                  selectedIndex === index ? 'bg-gray-50 dark:bg-gray-700' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <MapPin 
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      selectedIndex === index 
                        ? 'text-[var(--accent-primary)]' 
                        : 'text-gray-400'
                    }`} 
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {cityName || result.display_name.split(',')[0]}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {state ? `${state}, ${result.address.country}` : result.address.country}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 truncate">
                      {formatDisplayName(result.display_name)}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
      
      {/* AGC: No results message */}
      {isOpen && !isSearching && query.length >= 3 && results.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4"
        >
          <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
            {t('wizard.location.noResults')}
          </p>
        </div>
      )}
      
      {/* AGC: Hint for minimum characters */}
      {query.length > 0 && query.length < 3 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {t('common.search')}: {3 - query.length} {t('common.characters')} {t('common.required').toLowerCase()}
        </p>
      )}
    </div>
  )
}

export default AddressAutocomplete
